import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import { trackEvent } from '../utils/analytics';

const RETRO_RED = '#FF0A33';
const RETRO_BLUE = '#00F3FF';
const RETRO_YELLOW = '#FFE600';

const matrixRain = keyframes`
  0% {
    background-position: 0% 0%;
    opacity: 0.3;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    background-position: 0% 100%;
    opacity: 0.3;
  }
`;

const floatingSymbols = keyframes`
  0%, 100% {
    opacity: 0.2;
    transform: translateY(0);
  }
  50% {
    opacity: 0.4;
    transform: translateY(-20px);
  }
`;

const flicker = keyframes`
  0%, 100% { opacity: 1; }
  8% { opacity: 0.8; }
  10% { opacity: 0.4; }
  12% { opacity: 0.8; }
  14% { opacity: 0.9; }
  16% { opacity: 1; }
  90% { opacity: 0.8; }
  92% { opacity: 0.5; }
  94% { opacity: 0.8; }
  96% { opacity: 1; }
`;

const redactAnimation = keyframes`
  0%, 100% { 
    clip-path: inset(0 0 0 0);
    background-position: -200% 0;
  }
  5% {
    clip-path: inset(10% 0 85% 0);
  }
  15% {
    clip-path: inset(65% 0 15% 0);
  }
  25% {
    clip-path: inset(25% 0 65% 0);
  }
  35% {
    clip-path: inset(85% 0 5% 0);
  }
  45% {
    clip-path: inset(45% 0 45% 0);
  }
  50% {
    clip-path: inset(0 0 0 0);
    background-position: 200% 0;
  }
`;

const scanline = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0.5;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0.5;
  }
`;

const glitch = keyframes`
  0% {
    text-shadow: 0.05em 0 0 ${RETRO_RED}, -0.05em -0.025em 0 ${RETRO_BLUE},
                 -0.025em 0.05em 0 ${RETRO_YELLOW};
  }
  14% {
    text-shadow: 0.05em 0 0 ${RETRO_RED}, -0.05em -0.025em 0 ${RETRO_BLUE},
                 -0.025em 0.05em 0 ${RETRO_YELLOW};
  }
  15% {
    text-shadow: -0.05em -0.025em 0 ${RETRO_RED}, 0.025em 0.025em 0 ${RETRO_BLUE},
                 -0.05em -0.05em 0 ${RETRO_YELLOW};
  }
  49% {
    text-shadow: -0.05em -0.025em 0 ${RETRO_RED}, 0.025em 0.025em 0 ${RETRO_BLUE},
                 -0.05em -0.05em 0 ${RETRO_YELLOW};
  }
  50% {
    text-shadow: 0.025em 0.05em 0 ${RETRO_RED}, 0.05em 0 0 ${RETRO_BLUE},
                 0 -0.05em 0 ${RETRO_YELLOW};
  }
  99% {
    text-shadow: 0.025em 0.05em 0 ${RETRO_RED}, 0.05em 0 0 ${RETRO_BLUE},
                 0 -0.05em 0 ${RETRO_YELLOW};
  }
  100% {
    text-shadow: -0.025em 0 0 ${RETRO_RED}, -0.025em -0.025em 0 ${RETRO_BLUE},
                 -0.025em -0.05em 0 ${RETRO_YELLOW};
  }
`;

const FloatingSymbol = styled.div`
  position: absolute;
  font-size: 1.2rem;
  color: ${RETRO_YELLOW}33;
  pointer-events: none;
  animation: ${floatingSymbols} 10s ease-in-out infinite;
  z-index: 1;
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #000;
  color: #fff;
  font-family: 'Share Tech Mono', monospace;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, 
      transparent 0%,
      rgba(255, 10, 51, 0.1) 50%,
      transparent 100%
    );
    animation: ${matrixRain} 20s linear infinite;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.15) 0px,
      rgba(0, 0, 0, 0.15) 1px,
      transparent 1px,
      transparent 2px
    );
    pointer-events: none;
  }
`;

const Terminal = styled.div`
  width: 90%;
  max-width: 900px;
  height: 80%;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid ${RETRO_RED};
  padding: 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(255, 10, 51, 0.3),
              inset 0 0 50px rgba(255, 10, 51, 0.1);
  animation: ${flicker} 10s infinite;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${RETRO_RED};
    box-shadow: 0 0 10px ${RETRO_RED};
    animation: ${scanline} 6s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 50% 50%, 
        transparent 0%,
        rgba(255, 230, 0, 0.03) 20%,
        transparent 30%
      );
    pointer-events: none;
    animation: ${flicker} 8s infinite;
  }
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 0.5rem;
  background: rgba(255, 10, 51, 0.1);
  border-bottom: 1px solid ${RETRO_RED};
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: ${RETRO_RED};
  text-transform: uppercase;
  letter-spacing: 2px;

  &::after {
    content: 'WARNING: ENCRYPTED DATA';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${RETRO_YELLOW};
    opacity: 0.5;
    animation: ${flicker} 4s infinite;
  }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 1em;
  background-color: ${RETRO_YELLOW};
  margin-left: 4px;
  animation: ${glitch} 1s step-end infinite;
`;

const GlitchText = styled(motion.div)<{ $redacted?: boolean }>`
  font-size: 1rem;
  color: ${props => props.$redacted ? RETRO_BLUE : '#fff'};
  position: relative;
  margin: 0.5rem 0;
  white-space: pre-wrap;
  text-shadow: 0 0 5px currentColor;
  animation: ${glitch} 3s infinite;

  ${props => props.$redacted && `
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: -5px;
      right: -5px;
      height: 2px;
      background: linear-gradient(90deg, 
        transparent,
        ${RETRO_YELLOW} 20%,
        ${RETRO_RED} 50%,
        ${RETRO_YELLOW} 80%,
        transparent
      );
      animation: ${redactAnimation} 4s infinite;
    }
  `}
`;

const Content = styled.div`
  height: 100%;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${RETRO_RED} transparent;
  padding-top: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${RETRO_RED};
    border-radius: 3px;
  }
`;

const CrypticPage: React.FC = () => {
  const [text, setText] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const messages = [
    "INITIALIZING SEQUENCE...",
    "DECODING PATTERNS...",
    "ANALYZING STRUCTURES...",
    "ACCESSING SECURE CHANNEL...",
    "[ACCESS GRANTED]",
    "",
    "ENCRYPTED MESSAGE DETECTED:",
    "",
    "STEP 1: MORSE CODE TRANSMISSION",
    "MORSE CODE SEQUENCE:",
    "..--- -.. ..--- -.. ..--- -.. ..--- -.. ..--- -.. ..--- -.. ...-- . ....- .---- ...-- ..... -.... ..-. -.... -.. -.... ----. -.... ...-- ...-- -.-. ..--- -.. ..--- -.. ..--- -.. ..--- -.. ..--- -.. ..--- -..",
    "",
    "HINT: CONVERT TO HEX, THEN ASCII",
    "EXPECTED PATTERN: [2D] -> [-]",
    "PROCESSING...",
    "",
    "STEP 2: TRANSPOSITION MATRIX",
    "ENCRYPTED TEXT DETECTED:",
    "' huii0J6cyMk- aH a3,nm ,z84ae,aa84pUigy 0a'",
    "",
    "HINT: USE THE TRANSPORTATION KEY",
    "COLUMNS = KEY LENGTH",
    "ORDER BY ASCII VALUES",
    "",
    "STEP 3: COORDINATES ACQUIRED",
    "SEEK THE BUILDING WHERE EAST MEETS WEST",
    "ANCIENT WISDOM LIES IN THE CHARACTERS",
    "",
    "STEP 4: FINAL GATEWAY",
    "APPEND THE LOCATION NAME TO THE URL PATH",
    "FORMAT: /{location_name}",
    "",
    "ALERT: SECURITY PROTOCOLS ACTIVE",
    "AUTHENTICATION REQUIRED",
    "SEEK THE TRUTH IN THE SIGNS",
    "",
    "CONNECTION TERMINATED_"
  ];

  const symbols = ['⌘', '◈', '◇', '⚠', '⚡', '☢', '⚝', '◉', '◎', '◈'];

  const isRedacted = (text: string) => {
    return text.includes('[REDACTED]') || 
           text.includes('[CLASSIFIED]') ||
           text.includes('[DATA EXPUNGED]') ||
           text.includes('MATRIX') ||
           /\d{2}\.\d{4}/.test(text);
  };

  useEffect(() => {
    if (currentIndex < messages.length) {
      const timer = setTimeout(() => {
        setText(prev => [...prev, messages[currentIndex]]);
        setCurrentIndex(prev => prev + 1);
      }, Math.random() * 500 + 500);

      return () => clearTimeout(timer);
    }
  }, [currentIndex]);

  // Track user interactions with the cryptic page
  useEffect(() => {
    // Track page view with additional context
    trackEvent('cryptic_page_view', { 
      access_method: 'direct',
      access_time: new Date().toISOString()
    });

    // Track user engagement time
    const startTime = Date.now();
    return () => {
      const engagementTime = Math.floor((Date.now() - startTime) / 1000);
      trackEvent('cryptic_page_engagement', { 
        time_spent_seconds: engagementTime,
        completed_viewing: engagementTime > 30
      });
    };
  }, []);

  // Track text selection events
  const handleTextSelection = () => {
    const selectedText = window.getSelection()?.toString();
    if (selectedText && selectedText.length > 5) {
      trackEvent('cryptic_text_selection', { 
        selection_length: selectedText.length,
        contains_numbers: /\d/.test(selectedText)
      });
    }
  };

  return (
    <Container onMouseUp={handleTextSelection}>
      {symbols.map((symbol, index) => (
        <FloatingSymbol
          key={index}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`
          }}
        >
          {symbol}
        </FloatingSymbol>
      ))}
      <Terminal>
        <Header>
          <span>TERMINAL://cryptic_sequence</span>
          <span>{new Date().toISOString()}</span>
        </Header>
        <Content>
          {text.map((line, index) => (
            <GlitchText
              key={index}
              $redacted={isRedacted(line)}
              data-text={line}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {line}
              {index === text.length - 1 && <Cursor />}
            </GlitchText>
          ))}
        </Content>
      </Terminal>
    </Container>
  );
};

export default CrypticPage; 